/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable fp/no-mutating-methods */
import { useTranslation } from 'next-i18next'
import { useState, useEffect } from 'react'

import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import VideoPlayerIcon from '~/components/Icons/svgs/VideoPlayerIcon'
import { LearningActivityStructure } from '~/statistics-v2/types/statistics.types'
import { Link } from '~/components/Link'

export const VideoWatchTimeCard = ({
  videos,
  videoWatchTime,
  subTopicPath,
}: {
  videos: LearningActivityStructure[]
  videoWatchTime: number
  subTopicPath: string
}): React.ReactElement => {
  const { t } = useTranslation('statisticsV2')
  const [isMounted, setIsMounted] = useState(false)
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const totalWatchedVideos = videos.filter((v) => v.isComplete).length

  useEffect(() => {
    const timer = setTimeout(() => setIsMounted(true), 50)
    return () => clearTimeout(timer)
  }, [])

  const totalVideos = videos.length || 1
  const radius = 80
  const center = 100

  const segments = videos.map((video, index) => {
    const angleStep = 360 / totalVideos
    const startAngle = index * angleStep - 90
    const endAngle = (index + 1) * angleStep - 90

    const x1 = center + radius * Math.cos((Math.PI * startAngle) / 180)
    const y1 = center + radius * Math.sin((Math.PI * startAngle) / 180)
    const x2 = center + radius * Math.cos((Math.PI * endAngle) / 180)
    const y2 = center + radius * Math.sin((Math.PI * endAngle) / 180)

    const largeArcFlag = angleStep > 180 ? 1 : 0

    const pathData = `
      M ${center} ${center}
      L ${x1} ${y1}
      A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}
      Z
    `

    return {
      pathData,
      isWatched: video.isComplete,
      index,
      url: video.id ? video.id.replace('_video', '') : '',
    }
  })

  return (
    <div className="flex flex-col items-center lg:items-start gap-4 w-full">
      <h2 className="text-white text-xl lg:text-2xl font-bold">
        {t('videoWatched')} {totalWatchedVideos}/{videos.length}
      </h2>
      <div className="flex justify-center w-full">
        <div
          className={`w-full p-4 rounded-4xl bg-purple-darker/50 border border-blue-pastel shadow-2xl transition-all duration-700 ease-out transform ${
            isMounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          } hover:border-blue-pastel group relative overflow-hidden`}
        >
          <div className="relative z-10 flex flex-col items-center justify-center">
            <div className="relative w-30 lg:w-60 h-30 lg:h-60 ">
              <svg className="w-full h-full drop-shadow-purple" viewBox="0 0 200 200">
                <circle
                  className="opacity-10"
                  cx={center}
                  cy={center}
                  fill="transparent"
                  r={radius}
                  stroke="white"
                  strokeWidth={0.5}
                />

                {segments.map((seg, i) => (
                  <Link key={seg.pathData} to={`${subTopicPath}/${seg.url}`}>
                    <path
                      className={`transition-all duration-300 cursor-pointer origin-center ${
                        isMounted ? 'opacity-100' : 'opacity-0'
                      } ${hoveredIndex === i ? 'scale-110' : ''}`}
                      d={seg.pathData}
                      fill={seg.isWatched ? '#E3FFA0' : 'transparent'}
                      key={i}
                      onMouseEnter={() => setHoveredIndex(i)}
                      onMouseLeave={() => setHoveredIndex(null)}
                      stroke="white"
                      strokeWidth={1}
                      style={{
                        transitionDelay: isMounted ? '0s' : `${0.1 + i * 0.05}s`,
                        fillOpacity: seg.isWatched ? (hoveredIndex === i ? 1 : 0.8) : hoveredIndex === i ? 0.3 : 0.05,
                        transformOrigin: '100px 100px',
                      }}
                    />
                  </Link>
                ))}

                {videos.map((_, i) => {
                  const angle = (i * 360) / totalVideos - 90
                  const x = center + radius * Math.cos((Math.PI * angle) / 180)
                  const y = center + radius * Math.sin((Math.PI * angle) / 180)
                  return (
                    <line
                      className="opacity-20 pointer-events-none"
                      key={`line-${i + 1}`}
                      stroke="white"
                      strokeWidth={0.5}
                      x1={center}
                      x2={x}
                      y1={center}
                      y2={y}
                    />
                  )
                })}
              </svg>
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <SVGIcon className="text-white w-9 lg:w-17" icon={VideoPlayerIcon} size={66} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
